import React, { useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { User, Lock, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import authService from "../../services/authService";
import { useUserDispatch } from "../../context";

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useUserDispatch();
  const apolloClient = useApolloClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({ id: "", userPw: "" });

  useEffect(() => {
    authService.setApolloClient(apolloClient);

    if (authService.isAuthenticated()) {
      navigate("/dashboard/main");
    }
  }, [apolloClient, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.id.trim()) {
      setError("아이디를 입력해주세요");
      return;
    }
    if (!formData.userPw.trim()) {
      setError("비밀번호를 입력해주세요");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await authService.managerLogin(formData.id, formData.userPw);

      if (result.success) {
        const userData = result.data.user;

        dispatch({
          type: "LOGIN",
          data: {
            userId: userData.id,
            userName: userData.userName,
            authority: userData.authority,
          },
        });

        navigate("/dashboard/main");
      } else {
        setError(result.error || "로그인에 실패하였습니다");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("로그인 중 오류가 발생했습니다");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-[420px] shadow-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-xl font-semibold">
            관리자 로그인
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="id">아이디</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="id"
                  name="id"
                  type="text"
                  placeholder="아이디 입력"
                  value={formData.id}
                  onChange={handleChange}
                  className="h-11 pl-10"
                  autoComplete="off"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="userPw">비밀번호</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="userPw"
                  name="userPw"
                  type="password"
                  placeholder="비밀번호 입력"
                  value={formData.userPw}
                  onChange={handleChange}
                  className="h-11 pl-10"
                  autoComplete="off"
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            <Button
              type="submit"
              size="lg"
              className="mt-2 w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  로그인 중...
                </>
              ) : (
                "로그인"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
